import {CheckCircle2, Clock3, LogOut, Play, Save} from 'lucide-react'
import React, {FC, useId} from 'react'
import {createPortal} from 'react-dom'
import {useIntl} from 'react-intl'

export type SaveLaterStatus = 'idle' | 'saving' | 'saved' | 'error'

type ActionProps = {
  status: SaveLaterStatus
  disabled?: boolean
  onSave: () => void
}

/**
 * "Save and finish later" (handover, Point 5): a secondary action on every onboarding screen, with
 * a Saving… / All changes saved status. A failed save says so and offers Retry; the saved
 * confirmation is never shown before the save succeeded.
 */
export const SaveAndFinishLaterAction: FC<ActionProps> = ({status, disabled, onSave}) => {
  const intl = useIntl()
  const t = (id: string) => intl.formatMessage({id: `ONBOARDING_WIZARD.SAVE_LATER.${id}`})
  return (
    <div className='onboarding-save-later'>
      {status === 'saving' && (
        <span className='onboarding-save-later__status' role='status'>
          <span className='spinner-border spinner-border-sm' aria-hidden />
          {t('SAVING')}
        </span>
      )}
      {status === 'saved' && (
        <span className='onboarding-save-later__status' role='status'>
          <CheckCircle2 size={16} aria-hidden />
          {t('SAVED')}
        </span>
      )}
      {status === 'error' && (
        <span className='onboarding-save-later__status is-error' role='alert'>
          {t('FAILED')}
          <button type='button' className='onboarding-save-later__retry' onClick={onSave}>
            {t('RETRY')}
          </button>
        </span>
      )}
      <button
        type='button'
        className='onboarding-save-later__btn'
        onClick={onSave}
        disabled={disabled || status === 'saving'}
      >
        <Save size={16} aria-hidden />
        {t('ACTION')}
      </button>
    </div>
  )
}

type ModalProps = {
  open: boolean
  onKeepWorking: () => void
  onExit: () => void
}

/** Shown only after the save succeeded. */
export const SaveAndFinishLaterModal: FC<ModalProps> = ({open, onKeepWorking, onExit}) => {
  const intl = useIntl()
  const t = (id: string) => intl.formatMessage({id: `ONBOARDING_WIZARD.SAVE_LATER.${id}`})
  const titleId = useId()
  if (!open) return null
  return createPortal(
    <div className='ta-dialog-root'>
      <div className='ta-dialog-backdrop' onClick={onKeepWorking} aria-hidden />
      <div className='ta-dialog' role='dialog' aria-modal='true' aria-labelledby={titleId}>
        <div className='ta-dialog__head'>
          <span
            className='ta-dialog__icon'
            style={{background: 'rgba(23,168,107,0.14)', color: '#17a86b'}}
          >
            <CheckCircle2 size={20} aria-hidden />
          </span>
          <h2 id={titleId}>{t('MODAL_TITLE')}</h2>
        </div>
        <p className='ta-dialog__text'>{t('MODAL_TEXT')}</p>
        <div className='ta-dialog__actions'>
          <button type='button' className='ta-btn ta-btn--ghost' onClick={onExit}>
            {t('EXIT')}
          </button>
          <button
            type='button'
            className='ta-btn ta-btn--primary'
            onClick={onKeepWorking}
            autoFocus
          >
            {t('KEEP_WORKING')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

/**
 * Where "Exit setup" leads. Until onboarding is complete the portal routes every page back to the
 * wizard, so the exit destination is this paused state, with a clear Resume setup action (and Log
 * out for leaving the device) — exiting does not log the user out.
 */
export const SetupPausedScreen: FC<{onResume: () => void; onLogout: () => void}> = ({
  onResume,
  onLogout,
}) => {
  const intl = useIntl()
  const t = (id: string) => intl.formatMessage({id: `ONBOARDING_WIZARD.SAVE_LATER.${id}`})
  return (
    <div className='onboarding-paused'>
      <Clock3 size={48} aria-hidden style={{color: '#1a6cf0'}} />
      <h2>{t('PAUSED_TITLE')}</h2>
      <p>{t('PAUSED_TEXT')}</p>
      <div className='onboarding-paused__actions'>
        <button type='button' className='ta-btn ta-btn--primary ta-btn--lg' onClick={onResume}>
          <Play size={18} aria-hidden />
          {t('RESUME')}
        </button>
        <button type='button' className='ta-btn ta-btn--ghost ta-btn--lg' onClick={onLogout}>
          <LogOut size={18} aria-hidden />
          {t('LOG_OUT')}
        </button>
      </div>
    </div>
  )
}
